import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Check, ArrowRight, FileSearch, ListChecks, PhoneCall, ScanSearch, CheckCircle2 } from "lucide-react";
import { PageLayout } from "@/components/PageLayout";
import { Reveal } from "@/components/Reveal";
import api, { formatApiError } from "@/lib/api";

const scope = [
  "Guided security posture questionnaire covering identity, devices, cloud, email, and backups",
  "External attack-surface scan of your public domains and services",
  "Review of your current security tooling and account hygiene",
  "Compliance quick-check against the framework you care about (SOC 2, HIPAA, PCI, or CIS)",
];

const deliverables = [
  { icon: ListChecks, title: "Top-10 risk findings", desc: "Your most important exposures, ranked by real-world impact — written in plain English, not scanner jargon." },
  { icon: FileSearch, title: "Prioritized fix list", desc: "A one-page remediation roadmap: what to fix first, what it takes, and what can wait." },
  { icon: PhoneCall, title: "30-minute readout call", desc: "A senior consultant walks you through the findings and answers your questions live." },
  { icon: ScanSearch, title: "Baseline scorecard", desc: "A simple security score you can re-run later to prove progress to leadership, insurers, or customers." },
];

const steps = [
  { n: "01", t: "Book & pay", d: "Reserve your review — we confirm scope and send the questionnaire the same week." },
  { n: "02", t: "We assess", d: "You spend ~45 minutes on the questionnaire; we run the external scan and analysis." },
  { n: "03", t: "Readout", d: "Within 5 business days you get the report and your 30-minute readout call." },
  { n: "04", t: "Next steps", d: "Fix items yourself, or credit 100% of the $499 toward a full assessment or managed security." },
];

const faqs = [
  { q: "What exactly does the $499 Baseline Review include?", a: "A guided security posture questionnaire, an external attack-surface scan of your public domains, a top-10 risk findings report in plain English, a prioritized fix list, and a 30-minute readout call with a senior consultant." },
  { q: "How long does it take?", a: "You'll have your report and readout call within 5 business days of completing the questionnaire, which takes about 45 minutes of your time." },
  { q: "Is this a penetration test?", a: "No. Penetration testing, internal network scanning, and remediation are separate engagements — the Baseline Review tells you whether you need them, and where." },
  { q: "What happens after the review?", a: "You can fix items yourself using our roadmap, or credit 100% of the $499 toward any full assessment, compliance readiness engagement, or advisory retainer with Aliwer." },
];

export default function BaselineReview() {
  const book = () => {
    document.getElementById("book")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <PageLayout
      seoTitle="$499 Cybersecurity Baseline Review | Aliwer"
      seoDescription="A fixed-scope, $499 cybersecurity baseline review for small teams: external attack-surface scan, top-10 risk findings, prioritized fix list, and a 30-minute expert readout."
      path="/baseline-review"
      seoJsonLd={{
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }}
      eyebrow="Entry offer"
      title="Know where you stand. In days, not months."
      subtitle="The Cybersecurity Baseline Review is a fixed-scope, $499 engagement that gives small teams a clear, prioritized picture of their security posture — with zero sales pressure."
      testid="baseline-review-page"
    >
      {/* Price + CTA */}
      <Reveal>
        <div className="mb-14 flex flex-col md:flex-row md:items-center gap-8 rounded-2xl border border-white/15 bg-surface p-8 md:p-10" data-testid="baseline-price-card">
          <div className="flex-1">
            <div className="flex items-baseline gap-3">
              <span className="font-display text-5xl tracking-tighter text-paper">$499</span>
              <span className="text-sm text-fog">one-time · fixed scope · no upsell required</span>
            </div>
            <p className="mt-4 text-sm text-fog leading-relaxed max-w-xl">
              Delivered within 5 business days of your completed questionnaire. If we find nothing worth
              fixing, we'll tell you that too — and 100% of the fee credits toward any future Aliwer engagement.
            </p>
          </div>
          <button
            onClick={book}
            data-testid="baseline-book-btn"
            className="group inline-flex items-center justify-center gap-2 rounded-full bg-paper text-black text-sm font-medium px-8 py-4 hover:bg-white transition-colors shrink-0"
          >
            Book your baseline review
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5" />
          </button>
        </div>
      </Reveal>

      <div className="grid gap-12 lg:grid-cols-[1fr_1.3fr] mb-16">
        {/* Scope */}
        <Reveal>
          <div data-testid="baseline-scope">
            <p className="text-xs uppercase tracking-[0.3em] text-fog mb-4">What's in scope</p>
            <h2 className="font-display text-2xl tracking-tight text-paper mb-6">Fixed scope, no surprises.</h2>
            <ul className="space-y-3.5">
              {scope.map((s) => (
                <li key={s} className="flex items-start gap-3 text-sm text-paper/90 leading-relaxed">
                  <Check size={16} className="text-fog mt-0.5 shrink-0" />
                  {s}
                </li>
              ))}
            </ul>
            <p className="mt-6 text-xs text-fog/80 leading-relaxed">
              Out of scope: penetration testing, internal network scanning, and remediation work — those are
              separate engagements we'll quote only if you want them.
            </p>
          </div>
        </Reveal>

        {/* Deliverables */}
        <Reveal delay={0.08}>
          <div data-testid="baseline-deliverables">
            <p className="text-xs uppercase tracking-[0.3em] text-fog mb-4">What you receive</p>
            <h2 className="font-display text-2xl tracking-tight text-paper mb-6">Four concrete deliverables.</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {deliverables.map((d) => (
                <div key={d.title} className="rounded-2xl border border-white/10 bg-surface p-6">
                  <d.icon size={18} className="text-paper mb-4" strokeWidth={1.4} />
                  <h3 className="text-sm font-medium text-paper mb-1.5">{d.title}</h3>
                  <p className="text-xs text-fog leading-relaxed">{d.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </div>

      {/* Process */}
      <Reveal>
        <div className="mb-16" data-testid="baseline-process">
          <p className="text-xs uppercase tracking-[0.3em] text-fog mb-4">How it works</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10 rounded-2xl overflow-hidden">
            {steps.map((s) => (
              <div key={s.n} className="bg-surface p-7">
                <span className="font-display text-3xl tracking-tighter text-fog/50">{s.n}</span>
                <h3 className="mt-4 text-sm font-medium text-paper">{s.t}</h3>
                <p className="mt-2 text-xs text-fog leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </Reveal>

      {/* Guest booking */}
      <Reveal>
        <div id="book" className="mb-16 scroll-mt-28">
          <GuestBookingForm />
        </div>
      </Reveal>

      {/* FAQ */}
      <Reveal>
        <div className="mb-16" data-testid="baseline-faq">
          <p className="text-xs uppercase tracking-[0.3em] text-fog mb-4">Common questions</p>
          <div className="rounded-2xl border border-white/10 overflow-hidden divide-y divide-white/10">
            {faqs.map((f) => (
              <div key={f.q} className="bg-black/40 p-6 sm:p-7">
                <h3 className="text-sm font-medium text-paper">{f.q}</h3>
                <p className="mt-2 text-sm text-fog leading-relaxed max-w-3xl">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </Reveal>

      {/* Who it's for + final CTA */}
      <Reveal>
        <div className="rounded-2xl border border-white/15 bg-surface p-8 md:p-10 flex flex-col md:flex-row md:items-center gap-8" data-testid="baseline-footer-cta">
          <div className="flex-1">
            <h2 className="font-display text-2xl tracking-tight text-paper">Built for teams of 5–200 without a security lead.</h2>
            <p className="mt-3 text-sm text-fog leading-relaxed max-w-2xl">
              If you've been asked for "proof of security" by a customer, insurer, or auditor — or you simply
              don't know where you stand — this is the fastest, cheapest way to find out. Not ready to commit?{" "}
              <Link to="/register" className="underline underline-offset-2 text-paper hover:text-fog transition-colors">
                Start with the free 20-minute consult
              </Link>{" "}
              instead.
            </p>
          </div>
          <button
            onClick={book}
            data-testid="baseline-book-btn-footer"
            className="group inline-flex items-center justify-center gap-2 rounded-full bg-paper text-black text-sm font-medium px-8 py-4 hover:bg-white transition-colors shrink-0"
          >
            Book for $499
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5" />
          </button>
        </div>
      </Reveal>
    </PageLayout>
  );
}

const SLOTS = ["09:00", "10:30", "12:00", "13:30", "15:00", "16:30"];

function GuestBookingForm() {
  const [form, setForm] = useState({ name: "", email: "", practice: "", phone: "", preferred_date: "", time_slot: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const minDate = new Date(Date.now() + 2 * 86400000).toISOString().slice(0, 10);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/baseline/book", form);
      toast.success(data.message);
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="rounded-2xl border border-white/15 bg-surface p-10 text-center" data-testid="baseline-booking-success">
        <CheckCircle2 size={30} className="mx-auto text-green-400" />
        <h2 className="mt-4 font-display text-2xl tracking-tight text-paper">Request received.</h2>
        <p className="mt-3 text-sm text-fog leading-relaxed max-w-md mx-auto">
          A confirmation is on its way to your inbox. We'll confirm your Baseline Review and send the
          questionnaire within 1 business day — no payment is taken until we confirm scope with you.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-2xl border border-white/15 bg-surface p-8 md:p-10" data-testid="baseline-booking-form">
      <h2 className="font-display text-2xl tracking-tight text-paper mb-2">Book your Baseline Review — no account needed</h2>
      <p className="text-sm text-fog mb-7 max-w-xl">
        Tell us where to reach you and we'll confirm within 1 business day. Payment is collected only after
        we confirm scope — never before.
      </p>
      <div className="grid gap-4 sm:grid-cols-2">
        <GField label="Name" required value={form.name} onChange={set("name")} testid="gb-name" />
        <GField label="Email" type="email" required value={form.email} onChange={set("email")} testid="gb-email" />
        <GField label="Practice / company" optional value={form.practice} onChange={set("practice")} testid="gb-practice" />
        <GField label="Phone" optional type="tel" value={form.phone} onChange={set("phone")} testid="gb-phone" />
        <div>
          <label className="text-xs uppercase tracking-widest text-fog">Preferred readout date <span className="text-fog/60 normal-case tracking-normal">(optional)</span></label>
          <input
            type="date"
            min={minDate}
            value={form.preferred_date}
            onChange={set("preferred_date")}
            data-testid="gb-date"
            className="mt-2 w-full bg-black border border-white/15 rounded-xl px-4 py-3 text-paper text-sm outline-none focus:border-white/50 transition-colors [color-scheme:dark]"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-fog">Time <span className="text-fog/60 normal-case tracking-normal">(optional, Central Time)</span></label>
          <select
            value={form.time_slot}
            onChange={set("time_slot")}
            data-testid="gb-slot"
            className="mt-2 w-full bg-black border border-white/15 rounded-xl px-4 py-3 text-paper text-sm outline-none focus:border-white/50 transition-colors"
          >
            <option value="" className="bg-black">Any time</option>
            {SLOTS.map((s) => (
              <option key={s} value={s} className="bg-black">{s}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="mt-4">
        <label className="text-xs uppercase tracking-widest text-fog">Anything we should know? <span className="text-fog/60 normal-case tracking-normal">(optional)</span></label>
        <textarea
          rows={3}
          value={form.message}
          onChange={set("message")}
          data-testid="gb-message"
          className="mt-2 w-full bg-black border border-white/15 rounded-xl px-4 py-3 text-paper text-sm outline-none focus:border-white/50 transition-colors resize-none"
        />
      </div>
      <button
        type="submit"
        disabled={submitting}
        data-testid="gb-submit"
        className="mt-6 w-full sm:w-auto rounded-full bg-paper text-black text-sm font-medium px-10 py-3.5 hover:bg-white transition-colors disabled:opacity-60"
      >
        {submitting ? "Sending…" : "Request my $499 Baseline Review"}
      </button>
      <p className="mt-4 text-xs text-fog/80 leading-relaxed max-w-xl">
        We use these details only to schedule and deliver your review — see our{" "}
        <Link to="/privacy" className="underline underline-offset-2 hover:text-paper transition-colors">Privacy Policy</Link>.
        Existing clients can also book from the <Link to="/client" className="underline underline-offset-2 hover:text-paper transition-colors">client portal</Link>.
      </p>
    </form>
  );
}

function GField({ label, type = "text", value, onChange, testid, required, optional }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-fog">
        {label}
        {required && <span aria-hidden="true" className="text-paper"> *</span>}
        {optional && <span className="text-fog/60 normal-case tracking-normal"> (optional)</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        data-testid={testid}
        className="mt-2 w-full bg-black border border-white/15 rounded-xl px-4 py-3 text-paper text-sm outline-none focus:border-white/50 transition-colors"
      />
    </div>
  );
}
